import GridViewIcon from "@mui/icons-material/GridView";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  Badge,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OrderCard from "../../components/order-card/OrderCard";
import OrderFilters from "../../components/order-filters/OrderFilters";
import OrderList from "../../components/order-list/OrderList";
import { getAllOrders, setFilters } from "../../reduxStore/slices/orderSlice";
import { useNotify } from "../../utils/snackbarUtils";
import {
  ORDER_PAYMENT,
  ORDER_TABS_CONFIG,
  ORDER_TYPES_VALUES,
} from "../../constants/enums";
import OrdersEmptyState from "./OrdersEmptyState";
import NoOrdersTodayState from "./NoOrdersTodayState ";

// Status color map
const statusColors = {
  Ready: { color: "success", label: "✔ Ready" },
  "In Progress": { color: "warning", label: "⏳ In Progress" },
  Completed: { color: "info", label: "✓ Completed" },
};

// Status key mapping
const statusKeyMap = {
  Ready: "ready",
  "In Progress": "inProgress",
  Completed: "completed",
  Cancelled: "cancelled",
};

export default function OrderScreen() {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    orderList: orders,
    totalCount,
    loading: isLoading,
    filters: allFilters,
  } = useSelector((state) => state.order);

  const storeId = user?.storeId;

  console.log("orders", orders);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [view, setView] = useState("card");

  console.log("filteredOrders", allFilters);

  const hasOrdersToday = orders.length > 0;
  const filteredOrders = orders; // currently same, future-safe
  const hasFilteredOrders = filteredOrders.length > 0 && !isLoading;

  const getOrders = async () => {
    try {
      const obj = {
        offset: 0,
        limit: 5000,
        store_id: storeId,
      };
      if (allFilters.status) {
        obj["status"] = allFilters.status;
      }
      if (allFilters.orderType !== 0) {
        obj["orderType"] = allFilters.orderType;
      }
      if (allFilters.paymentStatus !== 0) {
        obj["paymentStatus"] = allFilters.paymentStatus;
      }
      await dispatch(getAllOrders(obj)).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (storeId) {
      getOrders();
    }
  }, [storeId, dispatch, allFilters]);

  const handleTabChange = (event, newValue) => {
    dispatch(setFilters({ status: newValue, offset: 0 }));
  };

  const handlePaymentChange = (event) => {
    dispatch(setFilters({ paymentStatus: event.target.value, offset: 0 }));
  };

  const handleOrderTypeChange = (event) => {
    dispatch(setFilters({ orderType: event.target.value, offset: 0 }));
  };

  // Drawer Filters
  const [filters, setFilters1] = useState({
    status: {
      ready: true,
      inProgress: true,
      completed: true,
      cancelled: false,
    },
    orderType: { dineIn: true, delivery: true, takeAway: true },
    diningArea: {
      garden: false,
      hall: false,
      rooftop: false,
      privateRoom: false,
    },
    paymentStatus: { paid: false, unpaid: false, partial: false },
  });

  // Applied filters state
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const handleClear = () => {
    setFilters1({
      status: {
        ready: false,
        inProgress: false,
        completed: false,
        cancelled: false,
      },
      orderType: { dineIn: false, delivery: false, takeAway: false },
      diningArea: {
        garden: false,
        hall: false,
        rooftop: false,
        privateRoom: false,
      },
      paymentStatus: { paid: false, unpaid: false, partial: false },
    });
  };

  const handleApply = () => {
    setAppliedFilters(filters);
    setOpenDrawer(false);
  };

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const renderTab = (label, count, color) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{label}</Typography>
      <Box
        sx={{
          backgroundColor: color,
          color: "#fff",
          borderRadius: "12px",
          px: 1.2,
          py: 0.2,
          fontSize: "0.70rem",
          fontWeight: 600,
          minWidth: "24px",
          textAlign: "center",
        }}
      >
        {count}
      </Box>
    </Box>
  );

  return (
    <Box p={3}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Orders — Today
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {todayLabel}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title={"Refresh"}>
            <IconButton onClick={getOrders}>
              <RefreshOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              view === "card" ? "Switch to List View" : "Switch to Grid View"
            }
          >
            <IconButton
              onClick={() => setView(view === "card" ? "list" : "card")}
            >
              {view === "card" ? <ViewListIcon /> : <GridViewIcon />}
            </IconButton>
          </Tooltip>

          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel id="order-type-label">Payment</InputLabel>

            <Select
              labelId="order-type-label"
              value={allFilters.paymentStatus ?? 0}
              label="Order Type"
              onChange={handlePaymentChange}
            >
              <MenuItem value={0}>All</MenuItem>
              {ORDER_PAYMENT.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="order-type-label">Order Type</InputLabel>

            <Select
              labelId="order-type-label"
              value={allFilters.orderType}
              label="Order Type"
              onChange={handleOrderTypeChange}
            >
              <MenuItem value={0}>All</MenuItem>
              {ORDER_TYPES_VALUES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search by order ID"
            sx={{ width: 200, bgcolor: "#fff", borderRadius: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={"Filter"}>
            <Badge
              badgeContent={activeFiltersCount > 0 ? activeFiltersCount : null}
              color="primary"
            >
              <IconButton onClick={() => setOpenDrawer(true)}>
                <TuneIcon />
              </IconButton>
            </Badge>
          </Tooltip>
          {activeFiltersCount > 0 && (
            <Button
              variant="text"
              color="error"
              // onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box>
      <Box mb={3}>
        <Tabs
          value={allFilters.status}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
        >
          {ORDER_TABS_CONFIG.map(({ value, label, color }) => (
            <Tab
              key={value}
              value={value}
              label={renderTab(label, 5 || 0, color)}
            />
          ))}
        </Tabs>
      </Box>

      {/* CONTENT AREA */}
      <Box mt={2}>
        {!hasOrdersToday && !isLoading && totalCount === 0 ? (
          // 1️⃣ NO ORDERS AT ALL TODAY
          <Box
            sx={{
              height: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoOrdersTodayState variant="no-orders" date={todayLabel} />
          </Box>
        ) : !hasFilteredOrders && !isLoading ? (
          // 2️⃣ FILTER/TAB RESULTS EMPTY
          <Box
            sx={{
              height: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <OrdersEmptyState
              variant="filtered"
              tab={
                ORDER_TABS_CONFIG.find((tab) => tab.value === allFilters.status)
                  ?.label
              }
            />
          </Box>
        ) : view === "card" ? (
          // 3️⃣ CARD VIEW
          <Grid container spacing={1.2}>
            {orders.length > 0 &&
              orders.map((order) => <OrderCard key={order.id} order={order} />)}
          </Grid>
        ) : (
          // 4️⃣ LIST VIEW
          <Box maxWidth={800} mx="auto">
            <OrderList orders={orders} />
          </Box>
        )}
      </Box>

      {/* Filters Drawer */}
      <OrderFilters
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={handleApply}
        onClear={handleClear}
      />
    </Box>
  );
}
